import React, { useEffect, useState, useCallback } from 'react';
import useForm from '@utils/useForm';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import { Helpers } from '@utils/helpers';
import { GAAviva } from '@utils/gaAviva';
import { isEmpty, get, includes, lowerCase } from 'lodash';
import { useTheme, ITheme } from 'src/theme';
import { useAvivaSelector, useAvivaDispatch } from '@redux/hook';
import { useHistory } from 'react-router-dom';
import { AboutYouForm, AboutYouHealthForm } from '@models/DPI/AboutYou.model';
import { updateIncome, updateHealth, completeStep } from '@redux/slices/DPI/step3Slice';
import { postBMICheck, postSavingMyInfo } from '@api/index';
import { BMICheckRequest } from '@models/BMIRequest.model';

import { IMyInfoQuestionRequest } from '@models/MyInfoRequest.model';

import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import MultiSteps from '@components/MultiSteps';
import Heading from '@components/Heading';
import Input from '@components/Input';
import Dropdown from '@components/Dropdown';
import CheckboxList from '@components/CheckboxList';
import Checkbox from '@components/Checkbox';
import Text from '@components/Text';
import Grid from '@components/Grid';
import Cell from '@components/Cell';
import Button from '@components/Button';
import ContactBox from '@components/ContactBox';
import BulletList from '@components/BulletList';
import Bullet from '@components/Bullet';
import RadioButtonGroup from '@components/RadioButtonGroup';
import RadioButton from '@components/RadioButton';
import Divider from '@components/Divider';
import ContainerBlock from '@components/ContainerBlock';
import { validate, validateHealthForm } from './service';
import { setLoadingState } from '@redux/slices/loadingSlice';


const SmokerStatusContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const customRadioButton = css`
  @media screen and (max-width: 767px) {
    .radio-item {
      width: 100%;
      &:nth-child(odd) {
        &:after{
          width: 175px;
          height: 1px;
          bottom: 0;
          left: 50%;
          right: unset;
          top: unset;
          transform: translateX(-50%);
        }
      }
    }
    .radio-hover {
      bottom: auto;
      right: 0;
      height: 50%;
      width: auto;
    }
    .radio-hover.o0 {
      transform: translateY(100%);
    }
    .radio-hover.o100 {
      transform: translateY(0);
    }
  }
`
const page = 'About you';
export interface AboutYouProps {
  theme?: ITheme
}

const convertHealthQuestions = (values: AboutYouHealthForm, basicDetails: any) => {
  let question = `1,${values['isSmoker'] ? 'Y' : 'N'}|` +
    `7,${values['q1']}|` +
    `8,${values['q2']}|` +
    `9,${values['q3']}|` +
    `${values['isSmoker'] ? '10,' + values['lessThan40Sticks'] + '|' : ''}` +
    `11,${values['q4']}|` +
    `21,${values['q5']}|` +
    `12,${values['q6']}|` +
    `13,${values['q7']}|` +
    `14,${values['q8']}|` +
    `15,${values['q9']}|` +
    `16,${values['q10']}|` +
    `17,${values['q11']}` +
    `${(basicDetails?.gender === 'F') ? '|18,' + values['q12'] : ''}`;
  return `${question}`;
}

const trackOnload = (step: string | number = 0): void => {
  const GA = GAAviva.getInstance();
  let { form } = GA.digitalData;
  form.name = Number(step) === 1 ? 'Health Questions' : 'Income Questions';
  GA.trackPageLoad({ form });
}

const trackFormSubmmit = (error: string = ''): void =>  {
  const GA = GAAviva.getInstance();
  if (isEmpty(error)) {
    GA.trackFormSuccess({});
    return;
  }
  const { form } = GA.digitalData;
  form.error.messages = error;
  GA.trackFormFailed({ form });
}

function AboutYou(props: AboutYouProps) {
  console.log(`Aviva - ${page}`);
  const minStep = 0, maxStep = 1;

  const history = useHistory();
  const theme: ITheme = useTheme();
  const dispatch = useAvivaDispatch();

  const sourceOfFunds = useAvivaSelector(state => state.staticData.sourceOfFunds);
  const sourceOfWealths = useAvivaSelector(state => state.staticData.sourceOfWealths);
  const contactInformation = useAvivaSelector(state => state.staticData.contactInformation);
  const businesses = useAvivaSelector(state => state.staticData.businesses);

  const occupationSlice = useAvivaSelector(state => state.dpiStep1.occupation);
  const basicDetails = useAvivaSelector(state => state.dpiStep1.basicDetails);

  const retrieveMyInfo = useAvivaSelector(state => state.dpiStep2.retrievedMyInfo);
  const personalDetails = useAvivaSelector(state => state.dpiStep2.personalDetails);
  const isStep2Completed = useAvivaSelector(state => state.dpiStep2.isCompleted);


  const initialIncome = useAvivaSelector(state => state.dpiStep3.income) || {};
  const initialHealth = useAvivaSelector(state => state.dpiStep3.health) || {};
  const basicDetials = useAvivaSelector(state => state.dpiStep1.basicDetails);

  const transactionId= useAvivaSelector(state=>state.dpiStep1.transactionId)

  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const [isSmoker, setIsSmoker] = useState<boolean | undefined>(undefined);
  const [step, setStep] = useState<number>(minStep);

  const { values, errors, handleChange, formValid, patchValue } = useForm(new AboutYouForm({ ...initialIncome  }), () => { }, validate);

  const {
    values: valuesMore,
    errors: errorsMore,
    handleChange: handleChangeMore,
    formValid: formMoreValid,
    patchValue: patchFormMore,

  } = useForm(new AboutYouHealthForm({ ...initialHealth }), () => {}, validateHealthForm);



  const onChangeCheckBox = (item: any, type: string) => (event: any) => {
    const { value } = event.target;
    const currentValue = values[type];
    let nextValue = currentValue ? [...currentValue] : [];
    if (value) {
      nextValue.push(item.value);
    } else {
      nextValue = nextValue.filter((o: any) => o !== item.value);
    }
    patchValue({ [type]: nextValue });
  }

  const needToGoToContact = useCallback(() => {
    if (isSmoker && valuesMore['lessThan40Sticks'] === 'N') {
      return true;
    }

    const basedDeclinedAnswer = 
     valuesMore['q1'] === 'Y' 
      || valuesMore['q2'] === 'Y' 
      || valuesMore['q3'] === 'Y' 
      || valuesMore['q4'] === 'Y' 
      || valuesMore['q5'] === 'Y'
      || valuesMore['q6'] === 'Y' 
      || valuesMore['q7'] === 'Y' 
      || valuesMore['q8'] === 'Y' 
      || valuesMore['q9'] === 'Y' 
      || valuesMore['q10'] === 'Y'
      || valuesMore['q11'] === 'Y' 
      || valuesMore['q12'] === 'Y' ;

    if (basedDeclinedAnswer) {
      return true;
    }

    return false;

  }, [valuesMore, isSmoker]);

  const gotoContact = useCallback(() => {
    history.push('/dpi/contact')
  }, [history]);

  const checkBmi = useCallback(async () => {
    const { weight, height } = valuesMore;
    const dob = personalDetails?.dob;
    const age = Helpers.getAge(new Date(dob || ''));
    try {
      const payload = new BMICheckRequest({ weight, height, age, prodcode: 'DPI' });
      const response = await postBMICheck(payload);
      const status = get(response, 'data.status', '');
      console.debug('postBMICheck', response, status);
      return lowerCase(status) !== 'decline';
    } catch (error) {
      console.error(error);
      return true;
    }
   
  }, [valuesMore, personalDetails]);

  const updateMyInfoAndContinue = useCallback(async () => {
    const id = retrieveMyInfo?.id || 0;
    const healthQuestions = convertHealthQuestions(valuesMore, basicDetails);
    const businessNature = Number(values['nature']);
    const exactDutiesOccupation = values['duties'];
    const businessNatureOthers = values['otherBusiness'] || undefined;
    const annualIncome = values['income'];
    const height = Number(valuesMore['height']);
    const weight = Number(valuesMore['weight']);
    const occupation = occupationSlice?.desc;
    const smokingStatus = isSmoker ? 'Yes' : 'No';
    let sourceOfWealth = '';
    values['wealth'].forEach((item: string) => {
      const f_item = item === 'other' ? `OTH` : item;
      sourceOfWealth = sourceOfWealth ? `${sourceOfWealth}|${f_item},${item === 'other' ? values['otherWealth'] : 'Y'}` : `${f_item},${item === 'other' ? values['otherWealth'] : 'Y'}`;
    });
    let sourceOfFunds = '';
    values['fund'].forEach((item: string) => {
      const f_item = item === 'other' ? `OTH` : item;
      sourceOfFunds = sourceOfFunds ? `${sourceOfFunds}|${f_item},${item === 'other' ? values['otherFund'] : 'Y'}` : `${f_item},${item === 'other' ? values['otherFund'] : 'Y'}`;
    });

    const payload: IMyInfoQuestionRequest = { id, exactDutiesOccupation, businessNature, businessNatureOthers, sourceOfWealth, sourceOfFunds, healthQuestions, occupation, height, weight, annualIncome, smokingStatus, transactionId};

    try {
      const response = await postSavingMyInfo(payload);
      console.debug('Update my info', { payload, response });
      dispatch(completeStep(true));
      history.push('/dpi/declaration');

    } catch (error) {
      console.debug('Update my info', error);
      dispatch(completeStep(true));
      history.push('/dpi/declaration'); //bypass first
    }
  }, [retrieveMyInfo, history, valuesMore, dispatch, isSmoker, occupationSlice, values, basicDetails]);

  const checkNatureOfBusiness = useCallback(() => {
    if (values['nature'] === '02' || values['nature'] === '07' || values['nature'] === '08') {
      gotoContact();
      return false;
    }
    return true;
  }, [values,gotoContact ]);

  const onClickContinue = useCallback(async () => {
    if (step < maxStep) {
      if (!checkNatureOfBusiness()) {
        return;
      }
      dispatch(completeStep(true));
      dispatch(updateIncome(values));
      trackFormSubmmit();
      const next = step + 1;
      trackOnload(next);
      setStep(next);
      history.push(`/dpi/about-you?step=${next}`)
      Helpers.scrollToTop();
    } else {
      dispatch(setLoadingState(true));
      dispatch(updateHealth({ ...valuesMore }));
      if (!await checkBmi() || needToGoToContact()) {
        gotoContact();
        dispatch(setLoadingState(false));
        return;
      }
      trackOnload(step);
      trackFormSubmmit();
      await updateMyInfoAndContinue();
      dispatch(setLoadingState(false));
    }
  }, [checkBmi, dispatch, gotoContact, needToGoToContact, updateMyInfoAndContinue, step , values, valuesMore, history, checkNatureOfBusiness]);

  
  const onBack = () => {
    if (step === minStep) {
      history.push('/dpi/my-info?step=2')
    } else {
      const back = step - 1;
      trackOnload(back);
      setStep(back);
      history.push(`/dpi/about-you?step=${back}`)
      Helpers.scrollToTop();
    }
  }

  const onFocusOnOthers = (type: 'fund' | 'wealth'): void => {
    const currentValue = values[type];
    let nextValue;
    if (!currentValue) {
      nextValue = ['other'];
      patchValue({ [type]: nextValue });
    } else {
      if (!includes(currentValue, 'other') && Array.isArray(currentValue)) {
        nextValue = [ ...currentValue, 'other' ];
        patchValue({ [type]: nextValue });
      }
    }
    console.log(currentValue, values, type, nextValue);

  }

  const initStep = useCallback(() => {
    const urlParams: URLSearchParams = Helpers.getURLSearchParams();
    const step = urlParams.get('step') || 0;
    trackOnload(step);
    setStep(Number(step));
    
  }, [setStep]);


  useEffect(() => {
    if (!isPageLoaded) {
      const smoker = get(basicDetials, 'smoker', 'N');
      const isSmorking = smoker === 'Y';
      if (isSmoker === undefined) {
        setIsSmoker(isSmorking);
        patchFormMore({ isSmoker: isSmorking, gender: basicDetails?.gender });
      }
      if (!isStep2Completed && process.env.REACT_APP_INJECT_CHECK !== 'true') {
        dispatch(completeStep(false));
        history.push('/dpi/my-info');
      } else {
        initStep();
        setIsPageLoaded(true);
      }
      setIsPageLoaded(true);
    }
  }, [isPageLoaded, setIsPageLoaded, initStep, basicDetials, patchFormMore, setIsSmoker, isSmoker, dispatch, history, isStep2Completed, basicDetails]);

  const renderCheckboxes = (sources: any[], type: string) => sources.map((source, index: number) => {
    return (<Checkbox key={`${index}`} variant="border" checked={includes(values[type], source.value)}  onChange={onChangeCheckBox(source, type)}><Text>{source.label}</Text></Checkbox>)
  });

  console.log(valuesMore, values);

  return (
    <Container>
      <ContainerGrid>
        <FullGrid>
        <MultiSteps steps={5} step={3} label={`${step === 0 ? 'Income questions' : 'Health questions'}`} />
          <Heading margin="0 0 30px"> More about yourself </Heading>
        </FullGrid>
        <LeftGrid>

          {step === 0 && (
            <React.Fragment>
              <Heading variant="label"> Exact duties of occupation </Heading>
              {!errors?.duties?.isValid && errors?.duties?.message && <Bullet variant="error">{errors?.duties?.message}</Bullet>}
              <Input margin="0 0 30px" name="duties"  invalid={!isEmpty(errors?.duties?.message)} maxLength={98} value={values['duties']} onChange={handleChange} />
              <Heading variant="label"> Nature of employer&apos;s business </Heading>
              <Dropdown placeholder="Please select" source={businesses} bindId="code" bindLabel="description" name="nature" value={values['nature']} onChange={handleChange} />
              {values['nature'] === '12' && (
                <React.Fragment>
                  <Heading variant="label"> Others </Heading>
                  {!errors?.otherBusiness?.isValid && errors?.otherBusiness?.message && <Bullet variant="error">{errors?.otherBusiness?.message}</Bullet>}
                  <Input margin="0 0 30px" placeholder="Please specify" name="otherBusiness" invalid={!isEmpty(errors?.otherBusiness?.message)} maxLength={98} value={values['otherBusiness']} onChange={handleChange} />
                </React.Fragment>)
              }
              <Heading variant="label"> Please select your source of funds (Origin of the funds used to pay your premiums) </Heading>
              <CheckboxList>
                {renderCheckboxes(sourceOfFunds, 'fund')}
                {!errors?.otherFund?.isValid && errors?.otherFund?.message && <Bullet variant="error">{errors?.otherFund?.message}</Bullet>}
                <Grid columns={5}>
                  <Cell width={2}><Checkbox variant="border" checked={includes(values['fund'], 'other')} onChange={onChangeCheckBox({ value: 'other' }, 'fund')}><Text>Others</Text></Checkbox></Cell>
                  <Cell width={3}><Input maxLength={50} name="otherFund" invalid={!isEmpty(errors?.otherFund?.message)} onFocus={() => onFocusOnOthers('fund')} placeholder="Please specify" onChange={handleChange} value={values['otherFund']} /></Cell>
                </Grid>
              </CheckboxList>
              <Heading variant="label"> Please select your source of wealth (Where your wealth is derived from)</Heading>
              <CheckboxList>
                {renderCheckboxes(sourceOfWealths, 'wealth')}
                {!errors?.otherWealth?.isValid && errors?.otherWealth?.message && <Bullet variant="error">{errors?.otherWealth?.message}</Bullet>}
                <Grid columns={5}>
                <Cell width={2}><Checkbox variant="border" checked={includes(values['wealth'], 'other')} onChange={onChangeCheckBox({ value: 'other' }, 'wealth')}><Text>Others</Text></Checkbox></Cell>
                <Cell width={3}><Input maxLength={50} name="otherWealth" invalid={!isEmpty(errors?.otherWealth?.message)} onFocus={() => onFocusOnOthers('wealth')} placeholder="Please specify" onChange={handleChange} value={values['otherWealth']} /></Cell>
                </Grid>
              </CheckboxList>
              <Heading variant="label"> Your annual income </Heading>
              {!errors?.income?.isValid && errors?.income?.message && <Bullet variant="error">{errors?.income?.message}</Bullet>}
              <Input variant="prefix" type="number" name="income" invalid={!isEmpty(errors?.income?.message)} maxLength={17} value={values['income']} onChange={handleChange} boxLabel="S$" margin="0 0 40px"></Input>
              <Button label="Continue" onClick={onClickContinue} disabled={!formValid} margin="0 0 20px" />
            </React.Fragment>
          )}
          {step === 1 && (
            <React.Fragment>
              <Heading variant="label" margin="0 0 10px">
                Do you take part in or plan to participate in any of the following activities:
              </Heading>
              <Text margin="0 0 15px"> Scuba diving, skydiving or parachuting, mountain or rock climbing (excluding artificial wall climbing), private flying, motor sports or other extreme or hazardous activities? </Text>
              <RadioButtonGroup value={valuesMore['q1']} name="q1" onChange={handleChangeMore} >
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              <Heading variant="label" margin="0 0 15px">
                In the last 12 months, have you spent more than 90 days outside of your current country of residence (excluding holiday or leisure)?
              </Heading>
              <RadioButtonGroup value={valuesMore['q2']} name="q2" onChange={handleChangeMore} >
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              <Heading variant="label" margin="0 0 15px">
                In the next 12 months, do you plan to spend more than 90 days outside of your current country of residence (excluding holiday or leisure)?
              </Heading>
              <RadioButtonGroup margin="0 0 40px" marginTablet="0 0 60px" value={valuesMore['q3']} name="q3" onChange={handleChangeMore}>
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              <Heading variant="sub" margin="0 0 10px">Smoker status</Heading>
              <Divider size={1} margin="0 0 30px" color={theme.colors.grey[700]}></Divider>
              <ContainerBlock background={theme.colors.grey[200]} padding="18px 30px" paddingTablet="25px 30px" margin={isSmoker ? "0 0 30px" : "0 0 40px"} marginTablet={isSmoker ? "0 0 30px" : "0 0 60px"}>
                <SmokerStatusContainer >
                  <Text fontWeight={600}>Are you a smoker?</Text>
                  <Text fontWeight={300} fontSize={theme.fontSize.bigSub} color={theme.colors.grey[300]}>{isSmoker ? 'Yes' : 'No'}</Text>
                </SmokerStatusContainer>
              </ContainerBlock>
              {isSmoker && (
                <React.Fragment>
                  <Heading variant="label" margin="0 0 10px">
                    How many sticks do you smoke daily?
                  </Heading>
                  <Text margin="0 0 15px"> Including social smokers, cigar smokers or those who have given up within the last 12 months. </Text>
                  <RadioButtonGroup margin="0 0 40px" marginTablet="0 0 60px" value={valuesMore['lessThan40Sticks']} name="lessThan40Sticks" onChange={handleChangeMore} className={customRadioButton}>
                    <RadioButton text="Less than 40 sticks" value="Y" />
                    <RadioButton text="40 sticks and above" value="N" />
                  </RadioButtonGroup>

                </React.Fragment>

              )}
              <Heading variant="sub" margin="0 0 10px">Other health questions</Heading>
              <Divider size={1} margin="0 0 30px" color={theme.colors.grey[700]}></Divider>

              <Heading variant="label" margin="0 0 15px">Height</Heading>
              {!errorsMore?.height?.isValid && errorsMore?.height?.message && (<Bullet variant="error">{errorsMore?.height?.message}</Bullet>)}
              <Input variant="suffix" boxLabel="cm" maxLength={3} type="number" invalid={!isEmpty(errorsMore?.height?.message)} value={valuesMore['height']} name="height" onChange={handleChangeMore} ></Input>

              <Heading variant="label" margin="0 0 15px">Weight</Heading>
              {!errorsMore?.weight?.isValid && errorsMore?.weight?.message && (<Bullet variant="error">{errorsMore?.weight?.message}</Bullet>)}
              <Input variant="suffix" boxLabel="kg" invalid={!isEmpty(errorsMore?.weight?.message)} maxLength={3} type="number" value={valuesMore['weight']} name="weight" onChange={handleChangeMore}></Input>

              <Heading variant="label" margin="0 0 10px"> Do you drink more than 20 standard alcohol drinks per week?</Heading>
              <Text margin="0 0 15px">1 standard alcoholic drink equates to 330ml beer, 125ml glass of wine or 30ml nip of spirits</Text>
              <RadioButtonGroup value={valuesMore['q4']} name="q4" onChange={handleChangeMore} >
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              <Heading variant="label" margin="0 0 15px">Have you ever taken or used addictive or illegal drugs, or been treated for drug addiction or alcoholism? </Heading>
              <RadioButtonGroup  value={valuesMore['q5']} name="q5" onChange={handleChangeMore}>
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              <Heading variant="label" margin="0 0 15px">Do you have a regular doctor? </Heading>
              <RadioButtonGroup  value={valuesMore['q6']} name="q6" onChange={handleChangeMore}>
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              <Heading variant="label" margin="0 0 12px"> Have you experienced symptoms or received medical advice or had treatment for any of the following conditions (whether diagnosed or not)? </Heading>
              <BulletList variant="default" bottomMargin="small">
                <Bullet>Heart attack, chest pain or discomfort, irregular heart beat, heart valve disorder, heart murmur, palpitations or any other blood vessel or heart disease or disorder</Bullet>
                <Bullet>High blood pressure or high cholesterol</Bullet>
                <Bullet>Cancer, tumour, cyst, lump or growth of any kind including cancer screening tests that were not normal</Bullet>
                <Bullet>Diabetes, elevated or raised blood sugar, thyroid disorders or any other endocrine disease or disorder</Bullet>
                <Bullet>Asthma, bronchitis, pneumonia, tuberculosis, emphysema or any other breathing or lung disease or disorder</Bullet>
                <Bullet>Depression, anxiety, stress or any other mental or nervous disorder</Bullet>
                <Bullet>Arthritis, gout or any other disorder, pain or injury to the muscles, bones, tendons, limbs, joints, spine (back or neck)</Bullet>
                <Bullet>Stroke, epilepsy, fits, paralysis or weakness of limb, head injury or any other neurological disease or disorder</Bullet>
                <Bullet>Crohn&apos;s disease, ulcerative colitis, gastritis, stomach or duodenal ulcers, blood in stools or any other bowel, stomach or intestinal disease or disorder</Bullet>
                <Bullet>Hepatitis B or C, fatty liver, abnormal or elevated liver function, gallstones or any other liver or gallbladder disease or disorder</Bullet>
                <Bullet>Anaemia, thalassaemia, haemophilia or any other blood disease or disorder</Bullet>
                <Bullet>Kidney stones, kidney infection, urine abnormalities or any other kidney, bladder, prostate or gynaecological disease or disorder</Bullet>
                <Bullet>Eye, ear, nose or throat disease or disorder (excluding sight problems corrected by prescription lenses)</Bullet>
                <Bullet>Any other illness, disorder, operation, physical disability or injury not mentioned above</Bullet>
              </BulletList>
              <RadioButtonGroup value={valuesMore['q7']} name="q7" onChange={handleChangeMore} >
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              <Heading margin="0 0 15px" variant="label">Other than the conditions that you have already told us about, in the last 5 years have you had any abnormal medical test result from medical test(s) such as X-ray, ultrasound, imaging scan, biopsy, electrocardiogram (ECG), HIV test, blood or urine test, Covid-19 PCR, pap smear or mammogram?</Heading>
              <RadioButtonGroup value={valuesMore['q8']} name="q8" onChange={handleChangeMore} >
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              <Heading margin="0 0 15px" variant="label">Other than the conditions that you have already told us about, are you currently experiencing symptoms or considering seeking medical advice or treatment for your health other than minor illnesses such as cold or flu?</Heading>
              <RadioButtonGroup value={valuesMore['q9']} name="q9" onChange={handleChangeMore} >
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              <Heading margin="0 0 12px" variant="label">Has any of your natural parent or sibling been diagnosed with or died from any of the following before age 60?</Heading>
              <BulletList>
                <Bullet>Cancers of the bowel, colon, breast or ovary</Bullet>
                <Bullet>Diabetes mellitus</Bullet>
                <Bullet>Cardiomyopathy, coronary artery disease, heart attack, ischaemic heart disease, stroke</Bullet>
                <Bullet>Multiple Sclerosis, muscular dystrophy</Bullet>
                <Bullet>Alzheimer&apos;s disease, Huntington&apos;s disease, Parkinson&apos;s Disease</Bullet>
                <Bullet>Polycystic kidney disease</Bullet>
                <Bullet>Any other hereditary disease or disorder requiring regular consultation</Bullet>
              </BulletList>
              <RadioButtonGroup value={valuesMore['q10']} name="q10" onChange={handleChangeMore} >
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              <Heading margin="0 0 15px" variant="label">Have you or your spouse or partner been told to have, received any medical advice, counselling or treatment in connection with sexually transmitted disease, AIDS, AIDS related complex or any other AIDS related condition?</Heading>
              <RadioButtonGroup value={valuesMore['q11']} name="q11" onChange={handleChangeMore} >
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              {
                basicDetails?.gender === 'F' && (
                  <React.Fragment>
                    <Heading margin="0 0 15px" variant="label">Are you currently pregnant?</Heading>
                    <RadioButtonGroup value={valuesMore['q12']} name="q12" onChange={handleChangeMore} >
                      <RadioButton text="Yes" value="Y" />
                      <RadioButton text="No" value="N" />
                    </RadioButtonGroup>
                  </React.Fragment>
                )
              }
              <Button label="Continue" onClick={onClickContinue} disabled={!formMoreValid} margin="40px 0 20px" />
            </React.Fragment>
          )}
          <Button onClick={onBack} variant="secondary" label="Back" margin="0 0 40px"  />
        </LeftGrid>
        <EmptyGrid></EmptyGrid>
        <RightGrid>
          <ContactBox items={contactInformation} />
        </RightGrid>
      </ContainerGrid>


    </Container>
  );
};

export default AboutYou;